/* Iter 290: VersionGate — polls /api/v2/_meta/version once per minute. When
 * the deployed `iter` changes (= a new release has been pushed), display
 * a non-intrusive banner inviting the user to refresh. This prevents the
 * "page blanche / stale bundle" issue we saw on logyra.fr after a deploy
 * by giving users a one-click way to load the fresh bundle without having
 * to hard-refresh + unregister the Service Worker manually.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;
const POLL_MS = 60_000;

const VersionGate = () => {
  const [serverIter, setServerIter] = useState(null);
  const [bootIter, setBootIter] = useState(null);
  const [outdated, setOutdated] = useState(false);

  const reload = useCallback(async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      /* ignore */
    }
    // Hard reload bypassing cache
    window.location.reload();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchVersion = async () => {
      try {
        const res = await axios.get(`${API}/_meta/version`, { timeout: 5000 });
        if (cancelled) return;
        const iter = res?.data?.iter;
        if (!iter) return;
        setServerIter((prev) => {
          if (prev === null && bootIter === null) {
            setBootIter(iter);
          } else if (bootIter !== null && iter !== bootIter) {
            setOutdated(true);
          }
          return iter;
        });
      } catch {
        /* swallow */
      }
    };
    fetchVersion();
    const id = setInterval(fetchVersion, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [bootIter]);

  if (!outdated) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: '#0a1b3d',
        color: '#fff',
        padding: '12px 18px',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(0,0,0,.25)',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        fontSize: 14,
        maxWidth: 'calc(100vw - 32px)',
      }}
      data-testid="version-gate-banner"
    >
      <span>
        <strong>Nouvelle version disponible</strong>
        <span style={{ opacity: 0.8, marginLeft: 8 }}>
          (iter {serverIter})
        </span>
      </span>
      <button
        type="button"
        onClick={reload}
        style={{
          background: '#e7af19',
          color: '#0a1b3d',
          border: 'none',
          padding: '6px 14px',
          borderRadius: 8,
          fontWeight: 700,
          cursor: 'pointer',
        }}
        data-testid="version-gate-reload"
      >
        Rafraichir
      </button>
    </div>
  );
};

export default VersionGate;
